/**
 * CashOut V2 Calculations
 *
 * Pure functions for all CashOut V2 form calculations.
 * These match the Toast POS system data structure as specified in
 * docs/006-cashout-form-v2-specification.md
 *
 * All payment types use ADDITION for gratuity: Amount + Tip + Gratuity
 */

// Import the CashOutV2Calculated type from types to avoid duplicate exports
import type { CashOutV2Calculated } from "../types/cashOutV2";

// Re-export for consumers who import from utils
export type { CashOutV2Calculated } from "../types/cashOutV2";

/**
 * Toast Sales Data input fields (Section 1)
 */
export interface ToastSalesData {
  netSalesTotal: number;
  totalGratuityAndFees: number;
  taxes: number;
  giftCardSales: number;
  discounts: number;
  totalNonCashTips: number;
}

/**
 * Account entry with amount, tip, and gratuity
 */
export interface AccountEntry {
  amount: number;
  tip: number;
  gratuity: number;
}

/**
 * Complete input for CashOut V2 calculations
 */
export interface CashOutV2Input {
  // Section 1: Toast Sales Data
  netSalesTotal: number;
  totalGratuityAndFees: number;
  taxes: number;
  giftCardSales: number;
  discounts: number;
  totalNonCashTips: number;

  // Section 3: Account Info
  staffAccountAmount: number;
  staffAccountTip: number;
  staffAccountGratuity: number;
  houseAccountAmount: number;
  houseAccountTip: number;
  houseAccountGratuity: number;

  // Section 4: Payment Centres (Cash, Credit, Gift Card)
  cashAmount: number;
  cashTip: number;
  cashGratuity: number;
  creditAmount: number;
  creditTip: number;
  creditGratuity: number;
  giftCardAmount: number;
  giftCardTip: number;
  giftCardGratuity: number;
}

/**
 * Round to 2 decimal places for currency
 */
function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

// =====================================================
// Section 2: Calculated Sales Summary
// =====================================================

/**
 * Calculate Gross Sales
 * Formula: Net Sales + Total Gratuity and Fees + Taxes + Discounts
 */
export function calculateGrossSales(data: ToastSalesData): number {
  return roundCurrency(
    data.netSalesTotal +
      data.totalGratuityAndFees +
      data.taxes +
      data.discounts
  );
}

/**
 * Calculate Net Sales (Calculated Net Sales)
 * Formula: Gross Sales - Discounts
 */
export function calculateNetSales(
  grossSales: number,
  discounts: number
): number {
  return roundCurrency(grossSales - discounts);
}

/**
 * Calculate Complete Sales
 * Formula: Calculated Net Sales + Total Non-Cash Tips + Gift Card Sales
 */
export function calculateCompleteSales(
  calculatedNetSales: number,
  totalNonCashTips: number,
  giftCardSales: number
): number {
  return roundCurrency(calculatedNetSales + totalNonCashTips + giftCardSales);
}

// =====================================================
// Section 3 & 4: Account and Payment Centre Totals
// =====================================================

/**
 * Calculate Account Total (for Staff Account, House Account, Credit, Gift Card, House Accounts)
 * Formula: Amount + Tip + Gratuity (ADDITION)
 */
export function calculateAccountTotal(
  amount: number,
  tip: number,
  gratuity: number
): number {
  return roundCurrency(amount + tip + gratuity);
}

/**
 * Calculate Cash Total
 * Formula: Amount + Tip + Gratuity
 */
export function calculateCashTotal(
  amount: number,
  tip: number,
  gratuity: number
): number {
  return roundCurrency(amount + tip + gratuity);
}

/**
 * Calculate Payment Total (for Credit, Gift Card)
 * Formula: Amount + Tip + Gratuity (ADDITION)
 */
export function calculatePaymentTotal(
  amount: number,
  tip: number,
  gratuity: number
): number {
  return roundCurrency(amount + tip + gratuity);
}

// =====================================================
// Section 4: Derived Totals
// =====================================================

/**
 * Calculate Total Payment Centre
 * Formula: Cash Total + Credit Total + Gift Card Total + House Accounts Total
 */
export function calculateTotalPaymentCentre(
  cashTotal: number,
  creditTotal: number,
  giftCardTotal: number,
  houseAccountsTotal: number
): number {
  return roundCurrency(cashTotal + creditTotal + giftCardTotal + houseAccountsTotal);
}

/**
 * Calculate Total Tips from Payment Centres
 * Formula: Cash Tip + Credit Tip + Gift Card Tip + House Accounts Tip
 */
export function calculateTotalTipsPayment(
  cashTip: number,
  creditTip: number,
  giftCardTip: number,
  houseAccountsTip: number
): number {
  return roundCurrency(cashTip + creditTip + giftCardTip + houseAccountsTip);
}

// =====================================================
// Section 5: Reconciliation
// =====================================================

/**
 * Calculate Sales Reconciliation
 * Formula: Complete Sales - Total Payment Centre
 * Should equal 0 when balanced
 */
export function calculateSalesReconciliation(
  completeSales: number,
  totalPaymentCentre: number
): number {
  return roundCurrency(completeSales - totalPaymentCentre);
}

/**
 * Calculate Tips Reconciliation
 * Formula: Total Non-Cash Tips - Total Tips (Payment Centre)
 * Should equal 0 when balanced
 */
export function calculateTipsReconciliation(
  totalNonCashTips: number,
  totalTipsPayment: number
): number {
  return roundCurrency(totalNonCashTips - totalTipsPayment);
}

// =====================================================
// Section 6: House Calculations
// =====================================================

/**
 * Calculate True Sales
 * Formula: Calculated Net Sales - Staff Account Total - Total Gratuity and Fees
 */
export function calculateTrueSales(
  calculatedNetSales: number,
  staffAccountTotal: number,
  totalGratuityAndFees: number
): number {
  return roundCurrency(calculatedNetSales - staffAccountTotal - totalGratuityAndFees);
}

/**
 * Calculate House Tips
 * Formula: True Sales × House Tip Percentage
 * Note: Percentage should be passed as decimal (e.g., 0.09 for 9%)
 */
export function calculateHouseTips(
  trueSales: number,
  houseTipPercentage: number
): number {
  return roundCurrency(trueSales * houseTipPercentage);
}

// =====================================================
// Section 7: Final Calculation
// =====================================================

/**
 * Calculate Owed to Server/House
 * Formula: (-Total Non-Cash Tips + House Tips) + Cash Total - Total Gratuity and Fees
 *
 * Interpretation:
 * - Positive value → Amount is owed TO HOUSE (server pays house)
 * - Negative value → Amount is owed TO SERVER (house pays server)
 */
export function calculateOwedToServerHouse(
  totalNonCashTips: number,
  houseTips: number,
  cashTotal: number,
  totalGratuityAndFees: number
): number {
  return roundCurrency(
    -totalNonCashTips + houseTips + cashTotal - totalGratuityAndFees
  );
}

// =====================================================
// Master Calculation Function
// =====================================================

/**
 * Calculate all CashOut V2 derived fields from input values
 *
 * @param input - All input fields from the form
 * @param houseTipPercentage - House tip percentage as decimal (default: 0.09 for 9%)
 * @returns All calculated/derived fields
 */
export function calculateAllCashOutV2Fields(
  input: CashOutV2Input,
  houseTipPercentage: number = 0.09
): CashOutV2Calculated {
  // Section 2: Calculated Sales Summary
  const grossSales = calculateGrossSales({
    netSalesTotal: input.netSalesTotal,
    totalGratuityAndFees: input.totalGratuityAndFees,
    taxes: input.taxes,
    giftCardSales: input.giftCardSales,
    discounts: input.discounts,
    totalNonCashTips: input.totalNonCashTips,
  });

  const calculatedNetSales = calculateNetSales(grossSales, input.discounts);

  const completeSales = calculateCompleteSales(
    calculatedNetSales,
    input.totalNonCashTips,
    input.giftCardSales
  );

  // Section 3: Account Totals
  const staffAccountTotal = calculateAccountTotal(
    input.staffAccountAmount,
    input.staffAccountTip,
    input.staffAccountGratuity
  );

  const houseAccountTotal = calculateAccountTotal(
    input.houseAccountAmount,
    input.houseAccountTip,
    input.houseAccountGratuity
  );

  // Section 4: Payment Centre Totals
  const cashTotal = calculateCashTotal(
    input.cashAmount,
    input.cashTip,
    input.cashGratuity
  );

  const creditTotal = calculatePaymentTotal(
    input.creditAmount,
    input.creditTip,
    input.creditGratuity
  );

  const giftCardTotal = calculatePaymentTotal(
    input.giftCardAmount,
    input.giftCardTip,
    input.giftCardGratuity
  );

  // House Accounts derived from Staff Account + House Account (Section 3)
  const houseAccountsAmount = roundCurrency(
    input.staffAccountAmount + input.houseAccountAmount
  );
  const houseAccountsTip = roundCurrency(
    input.staffAccountTip + input.houseAccountTip
  );
  const houseAccountsGratuity = roundCurrency(
    input.staffAccountGratuity + input.houseAccountGratuity
  );
  const houseAccountsTotal = calculateAccountTotal(
    houseAccountsAmount,
    houseAccountsTip,
    houseAccountsGratuity
  );

  // Payment Centre Totals
  const totalPaymentCentre = calculateTotalPaymentCentre(
    cashTotal,
    creditTotal,
    giftCardTotal,
    houseAccountsTotal
  );

  const totalTipsPayment = calculateTotalTipsPayment(
    input.cashTip,
    input.creditTip,
    input.giftCardTip,
    houseAccountsTip
  );

  // Section 5: Reconciliation
  const salesReconciliation = calculateSalesReconciliation(
    completeSales,
    totalPaymentCentre
  );

  const tipsReconciliation = calculateTipsReconciliation(
    input.totalNonCashTips,
    totalTipsPayment
  );

  // Section 6: House Calculations
  const trueSales = calculateTrueSales(
    calculatedNetSales,
    staffAccountTotal,
    input.totalGratuityAndFees
  );

  const houseTips = calculateHouseTips(trueSales, houseTipPercentage);

  // Section 7: Owed to Server/House
  const owedToServerHouse = calculateOwedToServerHouse(
    input.totalNonCashTips,
    houseTips,
    cashTotal,
    input.totalGratuityAndFees
  );

  return {
    // Section 2
    grossSales,
    calculatedNetSales,
    completeSales,

    // Section 3
    staffAccountTotal,
    houseAccountTotal,

    // Section 4
    cashTotal,
    creditTotal,
    giftCardTotal,
    houseAccountsAmount,
    houseAccountsTip,
    houseAccountsGratuity,
    houseAccountsTotal,
    totalPaymentCentre,
    totalTipsPayment,

    // Section 5
    salesReconciliation,
    tipsReconciliation,

    // Section 6
    trueSales,
    houseTips,

    // Section 7
    owedToServerHouse,
  };
}
